/**
 * Where a question's image was cut from: a region of a page of the paper the
 * question was extracted from.
 *
 * Recorded by the import job for every crop it makes (`by: 'extraction'`) and by
 * the editor's "Crop from paper" (`by: 'author'`). It is what lets the editor
 * open the paper on the right page with the current crop already drawn.
 *
 * See docs/FeatureDesigns/question-image-recrop-target.md.
 */
import type { PageRect } from './pageAnnotations';

/** One image field on a question. Parts by `partId`, never by index —
 *  reordering parts must not move a region onto another part's diagram. */
export type QuestionImageSlot =
    | { kind: 'questionDiagram' }
    | { kind: 'solutionImage' }
    | { kind: 'optionImage'; option: string }
    | { kind: 'partDiagram'; partId: string }
    | { kind: 'partOptionImage'; partId: string; option: string }
    /** A figure the text places by marker — `figures[]` on the question, or on
     *  a part. By figure id, which is stable as the list is reordered. */
    | { kind: 'questionFigure'; figureId: string }
    | { kind: 'partFigure'; partId: string; figureId: string };

export interface QuestionSourceRegion {
    slot: QuestionImageSlot;
    /** The set whose retained paper the page belongs to. A question can sit in
     *  many sets and only one holds the paper, so this is not derivable. */
    questionSetId: string;
    /** Absolute and 1-based, like every page number on the import path. */
    page: number;
    /** Fractions of the UPRIGHT page — rotations applied, crops not — so a
     *  region can reach past the teacher's import crop. See `replayPageGeometry`. */
    rect: PageRect;
    by: 'extraction' | 'author';
}

/** Stable identity for a slot. Option labels compare case-insensitively: the
 *  import writes them lower-case and the save path upper-cases them. */
export function imageSlotKey(slot: QuestionImageSlot): string {
    switch (slot.kind) {
        case 'optionImage': return `option:${slot.option.toLowerCase()}`;
        case 'partDiagram': return `part:${slot.partId}`;
        case 'partOptionImage': return `part:${slot.partId}:option:${slot.option.toLowerCase()}`;
        case 'questionFigure': return `figure:${slot.figureId}`;
        case 'partFigure': return `part:${slot.partId}:figure:${slot.figureId}`;
        default: return slot.kind;
    }
}

/** Replace the region for `region.slot`, keeping every other slot's. */
export function upsertSourceRegion(
    regions: readonly QuestionSourceRegion[] | undefined,
    region: QuestionSourceRegion,
): QuestionSourceRegion[] {
    const key = imageSlotKey(region.slot);
    return [...(regions ?? []).filter(r => imageSlotKey(r.slot) !== key), region];
}

/** Drop the region for a slot — its image no longer came from the paper. */
export function removeSourceRegion(
    regions: readonly QuestionSourceRegion[] | undefined,
    slot: QuestionImageSlot,
): QuestionSourceRegion[] {
    const key = imageSlotKey(slot);
    return (regions ?? []).filter(r => imageSlotKey(r.slot) !== key);
}
